import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store/store';
import { fetchUser, clearError } from './store/slices/authSlice';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import AddUser from './pages/User/AddUser';
import EditUser from './pages/User/EditUser';
import Roles from './pages/Roles';
import AddRole from './pages/Role/AddRole';
import EditRole from './pages/Role/EditRole';
import Patients from './pages/Patients';
import AddPatient from './pages/Patient/AddPatient';
import EditPatient from './pages/Patient/EditPatient';
import ProfileLayout from "./pages/Patient/manage/ProfileLayout";
import EnquiryForm from "./pages/Patient/manage/EnquiryForm";
import AddPatientInquiry from "./pages/Patient/manage/AddPatientInquiry";
import Cases from "./pages/Patient/manage/Cases/CaseList";
import CaseProfile from './pages/Patient/manage/Cases/CaseProfile';
import Inquiry from "./pages/Inquiries";
import AddInquiry from "./pages/Inquiry/AddInquiry";
import EditInquiry from "./pages/Inquiry/EditInquiry";
import InquiryPrint from "./components/forms/Phase-1/InquiryFormPrint";
import NursingOPDPrint from "./components/forms/OPD/NursingOPDPrint";
import DoctorOPDPrint from "./components/forms/OPD/DoctorOPDPrint";
import PrescriptionSlipPrint from "./components/forms/OPD/PrescriptionSlipPrint";
import Consents from './pages/Consents';
import AddConsent from './pages/Consent/AddConsent';
import EditConsent from './pages/Consent/EditConsent';
import SessionCategories from './pages/SessionCategories';
import AddSessionCategory from './pages/SessionCategory/AddSessionCategory';
import EditSessionCategory from './pages/SessionCategory/EditSessionCategory';
import SessionTypes from './pages/SessionTypes';
import AddSessionType from './pages/SessionType/AddSessionType';
import EditSessionType from './pages/SessionType/EditSessionType';
import SubSessionTypes from './pages/SubSessionTypes';
import AddSubSessionType from './pages/SubSessionType/AddSubSessionType';
import EditSubSessionType from './pages/SubSessionType/EditSubSessionType';
import UserSessions from './pages/UserSessions';
import AddUserSession from './pages/UserSession/AddUserSession';
import EditUserSession from './pages/UserSession/EditUserSession';
import UserSessionPrint from './pages/UserSession/UserSessionPrint';
import FormLogs from './pages/Logs/FormLogs';
import AuditLogs from './pages/Logs/AuditLogs';
import ConsentPrint from "./components/forms/Phase-2/ConsentFormPrint";


import Injections from "./pages/Injections";
import AddInjection from "./pages/Injection/AddInjection";
import EditInjection from "./pages/Injection/EditInjection";
import InjectionFormPrint from "./components/forms/Phase-2/InjectionFormPrint";
import PersonalBelongings from "./pages/PersonalBelongings";
import AddPersonalBelonging from "./pages/Phase-04/PersonalBelonging/AddPersonalBelongings";
import EditPersonalBelonging from "./pages/Phase-04/PersonalBelonging/EditPersonalBelongings";
import PersonalBelongingPrint from "./components/forms/Phase-4/PersonalBelongingFormPrint";
import TentativeFamilyPlans from "./pages/TentativeFamilyPlans";
import AddTentativeFamilyPlan from "./pages/Phase-04/TentativeFamilyPlan/AddTentativeFamilyPlan";
import EditTentativeFamilyPlan from "./pages/Phase-04/TentativeFamilyPlan/EditTentativeFamilyPlan";
import PrintTentativeFamilyPlan from "./components/forms/Phase-4/TentativeFamilyPlanFormPrint";
import ADLRecordSheets from './pages/ADLRecordSheets';
import AddADLRecord from './pages/Phase-05/ADLsRecordSheet/AddADLRecord';
import EditADLRecord from './pages/Phase-05/ADLsRecordSheet/EditADLRecord';
import ADLRecordPrint from './components/forms/Phase-5/ADLsDailyRecordFormPrint';
import FamilyMeetingsMinutes from './pages/FamilyMeetingsMinutes';
import AddFamilyMeetingMinutes from './pages/Phase-05/FamilyMeetingMinutes/AddFamilyMeetingMinutes';
import EditFamilyMeetingMinutes from './pages/Phase-05/FamilyMeetingMinutes/EditFamilyMeetingMinutes';
import FamilyMeetingMinutesPrint from "./components/forms/Phase-5/FamilyMeetingMinutesFormPrint";
import IncidentReports from './pages/IncidentReports';
import AddIncidentReport from './pages/Phase-05/IncidentReport/AddIncidentReport';
import EditIncidentReport from './pages/Phase-05/IncidentReport/EditIncidentReport';
import IncidentReportPrint from './components/forms/Phase-5/IncidentReportFormPrint';
import IndividualisedRehabPlans from './pages/IndividualisedRehabPlans';
import AddIndividualisedRehabPlan from './pages/Phase-05/IndividualisedRehabPlan/AddIndividualisedRehabPlan';
import EditIndividualisedRehabPlan from './pages/Phase-05/IndividualisedRehabPlan/EditIndividualisedRehabPlan';
import IndividualRehabPrint from './components/forms/Phase-5/IndividualRehabPlanFormPrint';
import MedicationSheets from './pages/MedicationSheets';
import AddMedicationSheet from './pages/Phase-05/MedicationSheet/AddMedicationSheet';
import EditMedicationSheet from './pages/Phase-05/MedicationSheet/EditMedicationSheet';
import SanaNotes from './pages/SanaNotes';
import AddSanaNote from './pages/Phase-05/SanaNote/AddSanaNote';
import EditSanaNote from './pages/Phase-05/SanaNote/EditSanaNote';
import SanaNotesPrint from './components/forms/Phase-5/DrSanaNotesFormPrint';
import NursingRecords from './pages/NursingRecords';
import AddNursingRecord from './pages/Phase-05/NursingRecord/AddNursingRecord';
import EditNursingRecord from './pages/Phase-05/NursingRecord/EditNursingRecord';
import NursingRecordPrint from './components/forms/Phase-5/NursingNotesRecordFormPrint';
import ProgressNotes from './pages/ProgressNotes';
import AddProgressNote from './pages/Phase-05/ProgressNote/AddProgressNote';
import EditProgressNote from './pages/Phase-05/ProgressNote/EditProgressNote';
import ProgressNotePrint from './components/forms/Phase-5/ProgressNotesFormPrint';
import PROSAssessments from './pages/PROSAssessments';
import AddPROSAssessment from './pages/Phase-05/PROSAssessment/AddPROSAssessment';
import EditPROSAssessment from './pages/Phase-05/PROSAssessment/EditPROSAssessment';
import ProsFormPrint from '../src/components/forms/Phase-5/ProsFormPrint';
import DepotInjections from './pages/DepotInjections';
import AddDepotInjection from './pages/Phase-05/DepotInjectionChart/AddDepotInjection';
import EditDepotInjection from './pages/Phase-05/DepotInjectionChart/EditDepotInjection';
import DepotInjectionPrint from './components/forms/Phase-5/DepotInjectionFormPrint';
import VitalsMonitor from './pages/VitalsMonitorings';
import AddVitalsMonitor from './pages/Phase-05/VitalsMonitorChart/AddVitalsMonitor';
import EditVitalsMonitor from './pages/Phase-05/VitalsMonitorChart/EditVitalsMonitor';
import VitalsMonitorPrint from './components/forms/Phase-5/VitalsMonitoringFormPrint';
import DischargeAdvices from './pages/DischargeAdvices';
import AddDischargeAdvice from './pages/Phase-07/DischargeAdvice/AddDischargeAdvice';
import EditDischargeAdvice from './pages/Phase-07/DischargeAdvice/EditDischargeAdvice';
import DischargeAdvicePrint from './components/forms/Phase-7/DischargeAdviceFormPrint';
import DischargeSummaries from './pages/DischargeSummaries';
import AddDischargeSummary from './pages/Phase-07/DischargeSummary/AddDischargeSummary';
import EditDischargeSummary from './pages/Phase-07/DischargeSummary/EditDischargeSummary';
import CasesList from './pages/CaseList';
import MyProfile from './pages/MyProfile';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import EscortFormHome from './pages/EscortFormHome';
import AddEscortForm from './pages/EscortForm/AddEscortForm';
import EditEscortForm from './pages/EscortForm/EditEscortForm';
import EscortFormPrint from './components/forms/Escort/EscortFormPrint';
import ClientMovementRequestFormList from './pages/ClientMovementRequestForm/ClientMovementRequestFormList';
import AddClientMovementRequestForm from './pages/ClientMovementRequestForm/AddClientMovementRequestForm';
import EditClientMovementRequestForm from './pages/ClientMovementRequestForm/EditClientMovementRequestForm';
import ClientMovementRequestFormPrint from './components/forms/Escort/ClientMovementRequestFormPrint';
import Cros3FormList from './pages/Cros3Form/Cros3FormList';
import AddCros3Form from './pages/Cros3Form/AddCros3Form';
import EditCros3Form from './pages/Cros3Form/EditCros3Form';
import Cros3FormPrint from './components/forms/Phase-3/Cros3FormPrint';
import CaseManagementList from './pages/CaseManagement/CaseManagementList';
import AddCaseManagement from './pages/CaseManagement/AddCaseManagement';
import EditCaseManagement from './pages/CaseManagement/EditCaseManagement';
import CaseManagementPrint from './components/forms/CaseManagement/CaseManagementPrint';
import ProgressNotePrpList from './pages/ProgressNotePrp/ProgressNotePrpList';
import AddProgressNotePrp from './pages/ProgressNotePrp/AddProgressNotePrp';
import EditProgressNotePrp from './pages/ProgressNotePrp/EditProgressNotePrp';
import ProgressNotePrpPrint from './components/forms/Phase-3/ProgressNotePrpPrint';
import IndividualClientFileList from './pages/IndividualClientFile/IndividualClientFileList';
import AddIndividualClientFile from './pages/IndividualClientFile/AddIndividualClientFile';
import EditIndividualClientFile from './pages/IndividualClientFile/EditIndividualClientFile';
import IndividualClientFilePrint from './components/forms/Phase-3/IndividualClientFilePrint';
import IndividualRehabPlanTwoList from './pages/IndividualRehabPlanTwo/IndividualRehabPlanTwoList';
import AddIndividualRehabPlanTwo from './pages/IndividualRehabPlanTwo/AddIndividualRehabPlanTwo';
import EditIndividualRehabPlanTwo from './pages/IndividualRehabPlanTwo/EditIndividualRehabPlanTwo';
import IndividualRehabPlanTwoPrint from './components/forms/Phase-3/IndividualRehabPlanTwoPrint';
import LeapParticipationConsentList from './pages/LeapParticipationConsent/LeapParticipationConsentList';
import AddLeapParticipationConsent from './pages/LeapParticipationConsent/AddLeapParticipationConsent';
import EditLeapParticipationConsent from './pages/LeapParticipationConsent/EditLeapParticipationConsent';
import LeapParticipationConsentPrint from './components/forms/Phase-3/LeapParticipationConsentPrint';
import TransitionFormList from './pages/TransitionForm/TransitionFormList';
import AddTransitionForm from './pages/TransitionForm/AddTransitionForm';
import EditTransitionForm from './pages/TransitionForm/EditTransitionForm';
import TransitionFormPrint from './components/forms/Phase-3/TransitionFormPrint';
import ClientFeedbackList from './pages/ClientFeedback/ClientFeedbackList';
import AddClientFeedback from './pages/ClientFeedback/AddClientFeedback';
import EditClientFeedback from './pages/ClientFeedback/EditClientFeedback';
import ClientFeedbackPrint from './components/forms/Phase-4/ClientFeedbackPrint';
import FamilyFeedbackList from './pages/FamilyFeedback/FamilyFeedbackList';
import AddFamilyFeedback from './pages/FamilyFeedback/AddFamilyFeedback';
import EditFamilyFeedback from './pages/FamilyFeedback/EditFamilyFeedback';
import FamilyFeedbackPrint from './components/forms/Phase-4/FamilyFeedbackPrint';
import HomeVisitClinicalRecordHome from './pages/HomeVisitClinicalRecordHome';
import AddHomeVisitClinicalRecord from './pages/HomeVisitClinicalRecord/AddHomeVisitClinicalRecord';
import EditHomeVisitClinicalRecord from './pages/HomeVisitClinicalRecord/EditHomeVisitClinicalRecord';
import HomeVisitClinicalRecordPrint from './components/forms/Escort/HomeVisitClinicalRecordPrint';
import Anxieties from './pages/Anxieties';
import AddAnxiety from './pages/Phase-04/Anxiety/AddAnxiety';
import EditAnxiety from './pages/Phase-04/Anxiety/EditAnxiety';
import BDI from './pages/BDI';
import AddBDI from './pages/Phase-04/BDI2/AddBDI';
import EditBDI from './pages/Phase-04/BDI2/EditBDI';
import SuicideIntents from './pages/SuicideIntents';
import AddSuicideIntent from './pages/Phase-04/SuicideIntent/AddSuicideIntent';
import EditSuicideIntent from './pages/Phase-04/SuicideIntent/EditSuicideIntent';
import BPRS from './pages/BPRS';
import AddBPRS from './pages/Phase-04/BPRS/AddBPRS';
import EditBPRS from './pages/Phase-04/BPRS/EditBPRS';
import PHQ from './pages/PHQ';
import AddPHQ from './pages/Phase-04/PHQ/AddPHQ';
import EditPHQ from './pages/Phase-04/PHQ/EditPHQ';
import ObsessiveCompulsiveTest from './pages/ObsessiveCompulsiveTest';
import AddObsessiveCompulsiveTest from './pages/Phase-04/ObsessiveCompulsiveTest/AddObsessiveCompulsiveTest';
import EditObsessiveCompulsiveTest from './pages/Phase-04/ObsessiveCompulsiveTest/EditObsessiveCompulsiveTest';
import QOL from './pages/QOL';
import AddQol from './pages/Phase-04/QOL/AddQOL';
import EditQOL from './pages/Phase-04/QOL/EditQOL';
import YMRS from './pages/YMRS';
import AddYMRS from './pages/Phase-04/YMRS/AddYMRS';
import EditYMRS from './pages/Phase-04/YMRS/EditYMRS';
import Psychiatrists from './pages/Psychiatrists';
import AddPsych from './pages/Residential/Phase-03/PsychiatristOrder/AddPsych';
import EditPsych from './pages/Residential/Phase-03/PsychiatristOrder/EditPsych';
import Complaint from './pages/Complaints';
import AddComplaint from './pages/Residential/Phase-03/Complaint/AddComplaint';
import EditComplaint from './pages/Residential/Phase-03/Complaint/EditComplaint';
import HealthSafety from './pages/HealthSafety';
import AddHealthSafety from './pages/HomeVisit/HealthSafety/AddHealthSafety';
import EditHealthSafety from './pages/HomeVisit/HealthSafety/EditHealthSafety';
import TreatmentPlan from './pages/TreatmentPlans';
import AddTreatmentPlan from './pages/HomeVisit/TreatmentPlan/AddTreatmentPlan';
import EditTreatmentPlan from './pages/HomeVisit/TreatmentPlan/EditTreatmentPlan';
import IntegratedInitialAssessments from './pages/IntegratedAssessments';
import AddIntegratedInitialAssessment from './pages/IntegratedAssessment/AddIntegratedAssessment';
import EditIntegratedInitialAssessment from './pages/IntegratedAssessment/EditIntegratedAssessment';
import Lama from './pages/LamaForms';
import AddLama from './pages/Residential/Optional/Lama/AddLama';
import EditLama from './pages/Residential/Optional/Lama/EditLama';
import LamaFormPrint from "./components/forms/Optional/LamaFormPrint";
import Referrals from './pages/Referrals';
import AddReferral from './pages/Residential/Optional/Referral/AddReferral';
import EditReferral from './pages/Residential/Optional/Referral/EditReferral';
import ReferralFormPrint from './components/forms/Optional/ReferralFormPrint';
import Relocations from './pages/Relocations';
import AddRelocation from './pages/Residential/Optional/Relocation/AddRelocation';
import EditRelocation from './pages/Residential/Optional/Relocation/EditRelocation';
import SafetyPlans from './pages/SafetyPlans';
import AddSafetyPlan from './pages/Residential/Optional/SafetyPlan/AddSafetyPlan';
import EditSafetyPlan from './pages/Residential/Optional/SafetyPlan/EditSafetyPlan';
import ViolenceRisks from './pages/ViolenceRisks';
import AddViolenceRisk from './pages/Residential/Optional/ViolenceRisk/AddViolenceRisk';
import EditViolenceRisk from './pages/Residential/Optional/ViolenceRisk/EditViolenceRisk';
import SuicideDetails from './pages/SuicideDetails';
import AddSuicideDetail from './pages/Residential/Optional/SuicideDetail/AddSuicideDetail';
import EditSuicideDetail from './pages/Residential/Optional/SuicideDetail/EditSuicideDetail';
import ExerciseSchedules from './pages/ExerciseSchedules';
import AddExerciseSchedule from './pages/ExerciseSchedule/AddExerciseSchedule';
import EditExerciseSchedule from './pages/ExerciseSchedule/EditExerciseSchedule';
import SafetyPlansUrdu from './pages/SafetyPlansUrdu';
import AddSafetyPlanUrdu from './pages/Residential/Optional/SafetyPlanUrdu/AddSafetyPlanUrdu';
import EditSafetyPlanUrdu from './pages/Residential/Optional/SafetyPlanUrdu/EditSafetyPlanUrdu';
import RelocationFormPrint from './components/forms/Optional/RelocationFormPrint';
import SafetyPlanPrint from './components/forms/Optional/SafetyPlanPrint';
import SuicideFormPrint from './components/forms/Optional/SuicideFormPrint';
import ViolenceFormPrint from './components/forms/Optional/ViolenceFormPrint';
import ComplaintFormPrint from './components/forms/Phase-3/ComplaintFormPrint';
import PsychFormPrint from './components/forms/Phase-3/PsychFormPrint';
import ExerciseFormPrint from './components/forms/Phase-3/ExerciseFormPrint';
import MedicationSheetPrint from './components/forms/Phase-5/MedicationSheetFormPrint';
import AnxietyFormPrint from './components/forms/Phase-4/AnxietyFormPrint';
import BDIIIFormPrint from './components/forms/Phase-4/BDI-IIFormPrint';
import BeckSuicideFormPrint from './components/forms/Phase-4/BeckSuicideFormPrint';
import BPRSFormPrint from './components/forms/Phase-4/BPRSFormPrint';
import PHQFormPrint from './components/forms/Phase-4/PHQFormPrint';
import ObsessiveCompulsiveTestFormPrint from './components/forms/Phase-4/ObsessiveCompulsiveTestFormPrint';
import Qol2FormPrint from './components/forms/Phase-4/Qol2FormPrint';
import YMRSFormPrint from './components/forms/Phase-4/YMRSFormPrint';
import SafetyPlanUrduForm from './pages/Residential/Optional/SafetyPlanUrdu/SafetyPlanUrduForm';
import SafetyPlanUrduFormPrint from './components/forms/Optional/SafetyPlan(Urdu)FormPrint';
import DischargeSummaryForm from './pages/Phase-07/DischargeSummary/DischargeSummaryForm';
import DischargeSummaryFormPrint from './components/forms/Phase-7/DischargeSummaryFormPrint';
import IntegratedAssessmentFormPrint from './components/forms/Phase-2/IntegratedAssessmentFormPrint';
import NursingOPDAssessments from './pages/NursingOPDAssessments';
import AddNursingOPDAssessment from './pages/OPD/Nursing/AddNursingOPDAssessment';
import EditNursingOPDAssessment from './pages/OPD/Nursing/EditNursingOPDAssessment';
import DoctorOPDAssessments from './pages/DoctorOPDAssessments';
import AddDoctorOPDAssessment from './pages/OPD/Doctor/AddDoctorOPDAssessment';
import EditDoctorOPDAssessment from './pages/OPD/Doctor/EditDoctorOPDAssessment';
import GeneralReports from './pages/Reports/General/GeneralReports';
import TrackingMetricsReports from './pages/Reports/TrackingMetrics/TrackingMetricsReports';
import HomeVisitReports from './pages/Reports/HomeVisits/HomeVisitReports';
import OPDReports from './pages/Reports/OPD/OPDReports';
import SessionReports from './pages/Reports/Sessions/SessionReports';
import CaseManagementReport from './pages/Reports/CaseManagement/CaseManagementReport';
import FeedbackReports from './pages/Reports/Feedback/FeedbackReports';
import IndividualClientFileReport from './pages/Reports/IndividualClientFile/IndividualClientFileReport';
import ExerciseMonthlyReport from './pages/Reports/ExerciseMonthly/ExerciseMonthlyReport';
import ADLRecordReport from './pages/Reports/ADLRecord/ADLRecordReport';
import DrpClientFeedbackList from './pages/DrpClientFeedback/DrpClientFeedbackList';
import AddDrpClientFeedback from './pages/DrpClientFeedback/AddDrpClientFeedback';
import EditDrpClientFeedback from './pages/DrpClientFeedback/EditDrpClientFeedback';
import DrpClientFeedbackPrint from './components/forms/Phase-4/DrpClientFeedbackPrint';


const AppRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, token, error } = useSelector((state) => state.auth);

  useEffect(() => {
    if (token && isAuthenticated) {
      dispatch(fetchUser());
    }
  }, [dispatch, token, isAuthenticated]);

  return (
    <div className="App">
      {/*
        key={location.key} forces the matched page to REMOUNT on every navigation
        (link, breadcrumb, sidebar, and browser back/forward each get a unique
        history key). This guarantees a page's local state — especially form state
        seeded from location.state/useParams (patient_id, case_id, client name, …) —
        is always rebuilt for the CURRENT patient/case and can never carry over a
        previous patient's data across navigations. Fixes cross-patient data leaks.
      */}
      <Routes key={location.key}>
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requiredPermission="dashboard-view">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-profile"
          element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredPermission="users-manage">
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/add"
          element={
            <ProtectedRoute requiredPermission="users-create">
              <AddUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/edit/:id"
          element={
            <ProtectedRoute requiredPermission="users-edit">
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute requiredPermission="roles-manage">
              <Roles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles/add"
          element={
            <ProtectedRoute requiredPermission="roles-create">
              <AddRole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles/edit/:id"
          element={
            <ProtectedRoute requiredPermission="roles-edit">
              <EditRole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients"
          element={
            <ProtectedRoute requiredPermission="patients-manage">
              <Patients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients/add"
          element={
            <ProtectedRoute requiredPermission="patients-create">
              <AddPatient />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients/edit/:id"
          element={
            <ProtectedRoute requiredPermission="patients-edit">
              <EditPatient />
            </ProtectedRoute>
          }
        />
        <Route path="/patients/manage/:id" element={<ProfileLayout />}>
          <Route path="enquiry-form" element={<EnquiryForm />} />
          <Route path="add-inquiry" element={<AddPatientInquiry />} />
          <Route path="cases" element={<Cases />} />
        </Route>
        <Route
          path="/inquiries"
          element={
            <ProtectedRoute requiredPermission="patients-manage">
              <Inquiry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inquiry/add"
          element={
            <ProtectedRoute requiredPermission="patients-create">
              <AddInquiry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inquiry/edit/:id"
          element={
            <ProtectedRoute>
              <EditInquiry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inquiry/print/:id"
          element={
            <ProtectedRoute>
              <InquiryPrint />
            </ProtectedRoute>
          }
        />
        {/* Consent Routes (new) */}
        <Route
          path="/consents"
          element={
            <ProtectedRoute requiredPermission="patients-manage">
              <Consents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consent/add"
          element={
            <ProtectedRoute requiredPermission="patients-create">
              <AddConsent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consent/edit/:id"
          element={
            <ProtectedRoute>
              <EditConsent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consents/print/:id"
          element={
            <ProtectedRoute>
              <ConsentPrint />
            </ProtectedRoute>
          }
        />
        {/* Session Category Routes */}
        <Route
          path="/session-categories"
          element={
            <ProtectedRoute requiredPermission="session-categories-manage">
              <SessionCategories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/session-categories/add"
          element={
            <ProtectedRoute requiredPermission="session-categories-create">
              <AddSessionCategory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/session-categories/edit/:id"
          element={
            <ProtectedRoute requiredPermission="session-categories-edit">
              <EditSessionCategory />
            </ProtectedRoute>
          }
        />

        {/* Session Type Routes */}
        <Route
          path="/session-types"
          element={
            <ProtectedRoute requiredPermission="session-types-manage">
              <SessionTypes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/session-types/add"
          element={
            <ProtectedRoute requiredPermission="session-types-create">
              <AddSessionType />
            </ProtectedRoute>
          }
        />
        <Route
          path="/session-types/edit/:id"
          element={
            <ProtectedRoute requiredPermission="session-types-edit">
              <EditSessionType />
            </ProtectedRoute>
          }
        />

        {/* Sub Session Type Routes */}
        <Route
          path="/sub-session-types"
          element={
            <ProtectedRoute requiredPermission="sub-session-types-manage">
              <SubSessionTypes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sub-session-types/add"
          element={
            <ProtectedRoute requiredPermission="sub-session-types-create">
              <AddSubSessionType />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sub-session-types/edit/:id"
          element={
            <ProtectedRoute requiredPermission="sub-session-types-edit">
              <EditSubSessionType />
            </ProtectedRoute>
          }
        />
        {/* User Session Routes */}
        <Route
          path="/sessions"
          element={
            <ProtectedRoute requiredPermission="user-sessions-manage">
              <UserSessions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions/add"
          element={
            <ProtectedRoute requiredPermission="user-sessions-create">
              <AddUserSession />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions/edit/:id"
          element={
            <ProtectedRoute requiredPermission="user-sessions-edit">
              <EditUserSession />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions/print/:id"
          element={
            <ProtectedRoute requiredPermission="user-sessions-manage">
              <UserSessionPrint />
            </ProtectedRoute>
          }
        />
        {/* Form Logs and Audit Logs Routes */}
        <Route
          path="/logs/forms"
          element={
            <ProtectedRoute requiredPermission="form-logs-manage">
              <FormLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs/audit"
          element={
            <ProtectedRoute requiredPermission="audit-logs-manage">
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        {/* Injection Form Routes */}


        <Route
          path="/injections"
          element={
            <ProtectedRoute>
              <Injections />
            </ProtectedRoute>
          }
        />
        <Route
          path="/injections/add"
          element={
            <ProtectedRoute>
              <AddInjection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/injections/edit/:id"
          element={
            <ProtectedRoute>
              <EditInjection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/injections/print/:id"
          element={
            <ProtectedRoute>
              <InjectionFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Personal Belongings Form Routes */}
        <Route
          path="/personal-belongings"
          element={
            <ProtectedRoute>
              <PersonalBelongings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/personal-belongings/add"
          element={
            <ProtectedRoute>
              <AddPersonalBelonging />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/personal-belongings/edit/:id"
          element={
            <ProtectedRoute>
              <EditPersonalBelonging />
            </ProtectedRoute>
          }
        />
        <Route
          path="/personal-belongings/print/:id"
          element={
            <ProtectedRoute>
              <PersonalBelongingPrint />
            </ProtectedRoute>
          }
        />

        {/* Tentative Family Plan Routes */}
        <Route
          path="/phase04/tentative-family-plan"
          element={
            <ProtectedRoute>
              <TentativeFamilyPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/tentative-family-plan/add"
          element={
            <ProtectedRoute>
              <AddTentativeFamilyPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/tentative-family-plan/edit/:id"
          element={
            <ProtectedRoute>
              <EditTentativeFamilyPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/tentative-family-plan/print/:id"
          element={
            <ProtectedRoute>
              <PrintTentativeFamilyPlan />
            </ProtectedRoute>
          }
        />

        {/* ADL Record Routes */}
        <Route
          path="/phase05/adl-record-sheet"
          element={
            <ProtectedRoute>
              <ADLRecordSheets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/adl-record-sheet/add"
          element={
            <ProtectedRoute>
              <AddADLRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/adl-record-sheet/edit/:id"
          element={
            <ProtectedRoute>
              <EditADLRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/adl-record-sheet/print/:id"
          element={
            <ProtectedRoute>
              <ADLRecordPrint />
            </ProtectedRoute>
          }
        />
        {/* Family Meeting Minutes Routes */}
        <Route
          path="/phase05/family-meeting-minutes"
          element={
            <ProtectedRoute>
              <FamilyMeetingsMinutes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/family-meeting-minutes/add"
          element={
            <ProtectedRoute>
              <AddFamilyMeetingMinutes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/family-meeting-minutes/edit/:id"
          element={
            <ProtectedRoute>
              <EditFamilyMeetingMinutes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/family-meeting-minutes/print/:id"
          element={
            <ProtectedRoute>
              <FamilyMeetingMinutesPrint />
            </ProtectedRoute>
          }
        />
        {/* Incident Reports Routes */}
        <Route
          path="/phase04/incident-reports"
          element={
            <ProtectedRoute>
              <IncidentReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/incident-report/add"
          element={
            <ProtectedRoute>
              <AddIncidentReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/incident-report/edit/:id"
          element={
            <ProtectedRoute>
              <EditIncidentReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/incident-report/print/:id"
          element={
            <ProtectedRoute>
              <IncidentReportPrint />
            </ProtectedRoute>
          }
        />
        {/* Individualised Care Plans Routes */}
        <Route
          path="/phase05/individualised-rehab-plans"
          element={
            <ProtectedRoute>
              <IndividualisedRehabPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/individualised-rehab-plans/add"
          element={
            <ProtectedRoute>
              <AddIndividualisedRehabPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/individualised-rehab-plans/edit/:id"
          element={
            <ProtectedRoute>
              <EditIndividualisedRehabPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/individualised-rehab-plan/print/:id"
          element={
            <ProtectedRoute>
              <IndividualRehabPrint />
            </ProtectedRoute>
          }
        />
        {/* Medication Sheet Routes */}
        <Route
          path="/phase05/medication-sheets"
          element={
            <ProtectedRoute>
              <MedicationSheets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/medication-sheets/add"
          element={
            <ProtectedRoute>
              <AddMedicationSheet />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/medication-sheets/edit/:id"
          element={
            <ProtectedRoute>
              <EditMedicationSheet />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/medication-sheets/print/:id"
          element={
            <ProtectedRoute>
              <MedicationSheetPrint />
            </ProtectedRoute>
          }
        />
        {/* Sana Notes (Progress Notes Physician) Routes */}
        <Route
          path="/phase05/sana-notes"
          element={
            <ProtectedRoute>
              <SanaNotes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/sana-notes/add"
          element={
            <ProtectedRoute>
              <AddSanaNote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/sana-notes/edit/:id"
          element={
            <ProtectedRoute>
              <EditSanaNote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/sana-notes/print/:id"
          element={
            <ProtectedRoute>
              <SanaNotesPrint />
            </ProtectedRoute>
          }
        />
        {/* Nursing Records (Phase-05) */}
        <Route
          path="/phase05/nursing-records"
          element={
            <ProtectedRoute>
              <NursingRecords />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/nursing-records/add"
          element={
            <ProtectedRoute>
              <AddNursingRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/nursing-records/edit/:id"
          element={
            <ProtectedRoute>
              <EditNursingRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/nursing-records/print/:id"
          element={
            <ProtectedRoute>
              <NursingRecordPrint />
            </ProtectedRoute>
          }
        />
        {/* Progress Notes (Phase-05) */}
        <Route
          path="/phase05/progress-notes"
          element={
            <ProtectedRoute>
              <ProgressNotes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/progress-notes/add"
          element={
            <ProtectedRoute>
              <AddProgressNote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/progress-notes/edit/:id"
          element={
            <ProtectedRoute>
              <EditProgressNote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/progress-notes/print/:id"
          element={
            <ProtectedRoute>
              <ProgressNotePrint />
            </ProtectedRoute>
          }
        />
        {/* PROS Assessment Routes */}
        <Route
          path="/phase05/pros-assessments"
          element={
            <ProtectedRoute>
              <PROSAssessments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/pros-assessments/add"
          element={
            <ProtectedRoute>
              <AddPROSAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/pros-assessments/edit/:id"
          element={
            <ProtectedRoute>
              <EditPROSAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/pros-assessments/print/:id"
          element={
            <ProtectedRoute>
              <ProsFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Depot Injections (Phase-05) */}
        <Route
          path="/phase05/depot-injections"
          element={
            <ProtectedRoute>
              <DepotInjections />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/depot-injections/add"
          element={
            <ProtectedRoute>
              <AddDepotInjection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/depot-injections/edit/:id"
          element={
            <ProtectedRoute>
              <EditDepotInjection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/depot-injections/print/:id"
          element={
            <ProtectedRoute>
              <DepotInjectionPrint />
            </ProtectedRoute>
          }
        />
        {/* Vitals Monitoring Chart (Phase-05) */}
        <Route
          path="/reports/general"
          element={
            <ProtectedRoute>
              <GeneralReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/tracking-metrics"
          element={
            <ProtectedRoute>
              <TrackingMetricsReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/home-visits"
          element={
            <ProtectedRoute>
              <HomeVisitReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/opds"
          element={
            <ProtectedRoute>
              <OPDReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/sessions"
          element={
            <ProtectedRoute>
              <SessionReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/case-management"
          element={
            <ProtectedRoute>
              <CaseManagementReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/feedback"
          element={
            <ProtectedRoute requiredPermission="report-feedback-manage">
              <FeedbackReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/individual-client-file"
          element={
            <ProtectedRoute requiredPermission="report-individual-client-file-manage">
              <IndividualClientFileReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/exercise-monthly"
          element={
            <ProtectedRoute requiredPermission="report-exercise-monthly-manage">
              <ExerciseMonthlyReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/adl-record"
          element={
            <ProtectedRoute requiredPermission="report-adl-record-manage">
              <ADLRecordReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/vitals-monitor"
          element={
            <ProtectedRoute>
              <VitalsMonitor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/vitals-monitor/add"
          element={
            <ProtectedRoute>
              <AddVitalsMonitor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/vitals-monitor/edit/:id"
          element={
            <ProtectedRoute>
              <EditVitalsMonitor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase05/vitals-monitor/print/:id"
          element={
            <ProtectedRoute>
              <VitalsMonitorPrint />
            </ProtectedRoute>
          }
        />

        {/* Escort Form (Home Visitation Phase-01) */}
        <Route
          path="/home-visitation/escort-form"
          element={
            <ProtectedRoute>
              <EscortFormHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visitation/escort-form/add"
          element={
            <ProtectedRoute>
              <AddEscortForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visitation/escort-form/edit/:id"
          element={
            <ProtectedRoute>
              <EditEscortForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visitation/escort-form/print/:id"
          element={
            <ProtectedRoute>
              <EscortFormPrint />
            </ProtectedRoute>
          }
        />

        {/* Client Movement Request Form (Day Rehab & Residential Rehab Phase-03) */}
        <Route
          path="/phase03/escort-form"
          element={
            <ProtectedRoute requiredPermission="client-movement-request-form-manage">
              <ClientMovementRequestFormList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/escort-form/add"
          element={
            <ProtectedRoute requiredPermission="client-movement-request-form-create">
              <AddClientMovementRequestForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/escort-form/edit/:id"
          element={
            <ProtectedRoute requiredPermission="client-movement-request-form-edit">
              <EditClientMovementRequestForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/escort-form/print/:id"
          element={
            <ProtectedRoute requiredPermission="client-movement-request-form-manage">
              <ClientMovementRequestFormPrint />
            </ProtectedRoute>
          }
        />

        {/* CROS-3 Form (Day Rehab & Residential Rehab Phase-03) */}
        <Route
          path="/phase03/cros3-form"
          element={
            <ProtectedRoute requiredPermission="cros3-form-manage">
              <Cros3FormList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/cros3-form/add"
          element={
            <ProtectedRoute requiredPermission="cros3-form-create">
              <AddCros3Form />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/cros3-form/edit/:id"
          element={
            <ProtectedRoute requiredPermission="cros3-form-edit">
              <EditCros3Form />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/cros3-form/print/:id"
          element={
            <ProtectedRoute requiredPermission="cros3-form-manage">
              <Cros3FormPrint />
            </ProtectedRoute>
          }
        />

        {/* Progress Notes PRP (Residential Rehab Phase-03) */}
        <Route
          path="/phase03/progress-notes-prp"
          element={
            <ProtectedRoute requiredPermission="progress-note-prp-manage">
              <ProgressNotePrpList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/progress-notes-prp/add"
          element={
            <ProtectedRoute requiredPermission="progress-note-prp-create">
              <AddProgressNotePrp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/progress-notes-prp/edit/:id"
          element={
            <ProtectedRoute requiredPermission="progress-note-prp-edit">
              <EditProgressNotePrp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/progress-notes-prp/print/:id"
          element={
            <ProtectedRoute requiredPermission="progress-note-prp-manage">
              <ProgressNotePrpPrint />
            </ProtectedRoute>
          }
        />

        {/* Individual Client File (Day Rehab & Residential Rehab Phase-03) */}
        <Route
          path="/phase03/individual-client-file"
          element={
            <ProtectedRoute requiredPermission="individual-client-file-manage">
              <IndividualClientFileList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/individual-client-file/add"
          element={
            <ProtectedRoute requiredPermission="individual-client-file-create">
              <AddIndividualClientFile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/individual-client-file/edit/:id"
          element={
            <ProtectedRoute requiredPermission="individual-client-file-edit">
              <EditIndividualClientFile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/individual-client-file/print/:id"
          element={
            <ProtectedRoute requiredPermission="individual-client-file-manage">
              <IndividualClientFilePrint />
            </ProtectedRoute>
          }
        />

        {/* Individual Rehabilitation Plan (2) — Day Rehab Phase-04 & Residential Phase-03 */}
        <Route
          path="/individual-rehab-plan-two"
          element={
            <ProtectedRoute requiredPermission="individual-rehab-plan-two-manage">
              <IndividualRehabPlanTwoList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/individual-rehab-plan-two/add"
          element={
            <ProtectedRoute requiredPermission="individual-rehab-plan-two-create">
              <AddIndividualRehabPlanTwo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/individual-rehab-plan-two/edit/:id"
          element={
            <ProtectedRoute requiredPermission="individual-rehab-plan-two-edit">
              <EditIndividualRehabPlanTwo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/individual-rehab-plan-two/print/:id"
          element={
            <ProtectedRoute requiredPermission="individual-rehab-plan-two-manage">
              <IndividualRehabPlanTwoPrint />
            </ProtectedRoute>
          }
        />

        {/* LEAP Participation Consent — Residential Phase-03 */}
        <Route
          path="/phase03/leap-participation-consent"
          element={
            <ProtectedRoute requiredPermission="leap-participation-consent-manage">
              <LeapParticipationConsentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/leap-participation-consent/add"
          element={
            <ProtectedRoute requiredPermission="leap-participation-consent-create">
              <AddLeapParticipationConsent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/leap-participation-consent/edit/:id"
          element={
            <ProtectedRoute requiredPermission="leap-participation-consent-edit">
              <EditLeapParticipationConsent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/leap-participation-consent/print/:id"
          element={
            <ProtectedRoute requiredPermission="leap-participation-consent-manage">
              <LeapParticipationConsentPrint />
            </ProtectedRoute>
          }
        />

        {/* Transition Form — Residential Phase-03 */}
        <Route
          path="/phase03/transition-form"
          element={
            <ProtectedRoute requiredPermission="transition-form-manage">
              <TransitionFormList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/transition-form/add"
          element={
            <ProtectedRoute requiredPermission="transition-form-create">
              <AddTransitionForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/transition-form/edit/:id"
          element={
            <ProtectedRoute requiredPermission="transition-form-edit">
              <EditTransitionForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/transition-form/print/:id"
          element={
            <ProtectedRoute requiredPermission="transition-form-manage">
              <TransitionFormPrint />
            </ProtectedRoute>
          }
        />

        {/* Client Feedback — Residential Phase-04 */}
        <Route
          path="/phase04/client-feedback"
          element={
            <ProtectedRoute requiredPermission="client-feedback-manage">
              <ClientFeedbackList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/client-feedback/add"
          element={
            <ProtectedRoute requiredPermission="client-feedback-create">
              <AddClientFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/client-feedback/edit/:id"
          element={
            <ProtectedRoute requiredPermission="client-feedback-edit">
              <EditClientFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/client-feedback/print/:id"
          element={
            <ProtectedRoute requiredPermission="client-feedback-manage">
              <ClientFeedbackPrint />
            </ProtectedRoute>
          }
        />

        {/* Family Feedback — Residential Phase-04 */}
        <Route
          path="/phase04/family-feedback"
          element={
            <ProtectedRoute requiredPermission="family-feedback-manage">
              <FamilyFeedbackList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/family-feedback/add"
          element={
            <ProtectedRoute requiredPermission="family-feedback-create">
              <AddFamilyFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/family-feedback/edit/:id"
          element={
            <ProtectedRoute requiredPermission="family-feedback-edit">
              <EditFamilyFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/family-feedback/print/:id"
          element={
            <ProtectedRoute requiredPermission="family-feedback-manage">
              <FamilyFeedbackPrint />
            </ProtectedRoute>
          }
        />


        {/* Client Feedback & Discharge Interview — DRP Phase-04 */}
        <Route
          path="/phase04/client-feeback-discharge-interview"
          element={
            <ProtectedRoute requiredPermission="drp-client-feedback-manage">
              <DrpClientFeedbackList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/client-feeback-discharge-interview/add"
          element={
            <ProtectedRoute requiredPermission="drp-client-feedback-create">
              <AddDrpClientFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/client-feeback-discharge-interview/edit/:id"
          element={
            <ProtectedRoute requiredPermission="drp-client-feedback-edit">
              <EditDrpClientFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/client-feeback-discharge-interview/print/:id"
          element={
            <ProtectedRoute requiredPermission="drp-client-feedback-manage">
              <DrpClientFeedbackPrint />
            </ProtectedRoute>
          }
        />

        {/* Case Management Standalone Module */}
        <Route
          path="/case-management"
          element={
            <ProtectedRoute requiredPermission="case-management-manage">
              <CaseManagementList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/case-management/add"
          element={
            <ProtectedRoute requiredPermission="case-management-create">
              <AddCaseManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/case-management/edit/:id"
          element={
            <ProtectedRoute requiredPermission="case-management-edit">
              <EditCaseManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/case-management/print/:id"
          element={
            <ProtectedRoute requiredPermission="case-management-manage">
              <CaseManagementPrint />
            </ProtectedRoute>
          }
        />

        {/* Home Visit Clinical Record Form (Home Visitation Phase-01) */}
        <Route
          path="/home-visitation/clinical-record"
          element={
            <ProtectedRoute>
              <HomeVisitClinicalRecordHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visitation/clinical-record/add"
          element={
            <ProtectedRoute>
              <AddHomeVisitClinicalRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visitation/clinical-record/edit/:id"
          element={
            <ProtectedRoute>
              <EditHomeVisitClinicalRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visitation/clinical-record/print/:id"
          element={
            <ProtectedRoute>
              <HomeVisitClinicalRecordPrint />
            </ProtectedRoute>
          }
        />

        {/* Dicharge Advice (Phase-07) */}
        <Route
          path="/phase07/discharge-advice"
          element={
            <ProtectedRoute>
              <DischargeAdvices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase07/discharge-advice/add"
          element={
            <ProtectedRoute>
              <AddDischargeAdvice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase07/discharge-advice/edit/:id"
          element={
            <ProtectedRoute>
              <EditDischargeAdvice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase07/discharge-advice/print/:id"
          element={
            <ProtectedRoute>
              <DischargeAdvicePrint />
            </ProtectedRoute>
          }
        />

        {/* Discharge Summary (Phase-07) */}
        <Route
          path="/phase07/discharge-summary"
          element={
            <ProtectedRoute>
              <DischargeSummaries />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase07/discharge-summary/add"
          element={
            <ProtectedRoute>
              <AddDischargeSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase07/discharge-summary/edit/:id"
          element={
            <ProtectedRoute>
              <EditDischargeSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase07/discharge-summary/print/:id"
          element={
            <ProtectedRoute>
              <DischargeSummaryFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Cases List */}
        <Route
          path="/cases"
          element={
            <ProtectedRoute>
              <CasesList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cases/:caseId/profile"
          element={
            <ProtectedRoute>
              <CaseProfile />
            </ProtectedRoute>
          }
        />

        {/* Anxiety Form (Day Rehab: Phase-04) */}
        <Route
          path="/phase04/anxiety"
          element={
            <ProtectedRoute>
              <Anxieties />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/anxiety/add"
          element={
            <ProtectedRoute>
              <AddAnxiety />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/anxiety/edit/:id"
          element={
            <ProtectedRoute>
              <EditAnxiety />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/anxiety/print/:id"
          element={
            <ProtectedRoute>
              <AnxietyFormPrint />
            </ProtectedRoute>
          }
        />

        {/* Beck Depression Inventory-II (BDI-II) */}
        <Route
          path="/phase04/bdi"
          element={
            <ProtectedRoute>
              <BDI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/bdi/add"
          element={
            <ProtectedRoute>
              <AddBDI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/bdi/edit/:id"
          element={
            <ProtectedRoute>
              <EditBDI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/bdi/print/:id"
          element={
            <ProtectedRoute>
              <BDIIIFormPrint />
            </ProtectedRoute>
          }
        />

        {/* Beck’s Suicide Intent Scale */}
        <Route
          path="/phase04/suicide-intent"
          element={
            <ProtectedRoute>
              <SuicideIntents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/suicide-intent/add"
          element={
            <ProtectedRoute>
              <AddSuicideIntent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/suicide-intent/edit/:id"
          element={
            <ProtectedRoute>
              <EditSuicideIntent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/suicide-intent/print/:id"
          element={
            <ProtectedRoute>
              <BeckSuicideFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Brief Psychiatric Rating Scale (BPRS) */}
        <Route
          path="/phase04/bprs"
          element={
            <ProtectedRoute>
              <BPRS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/bprs/add"
          element={
            <ProtectedRoute>
              <AddBPRS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/bprs/edit/:id"
          element={
            <ProtectedRoute>
              <EditBPRS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/bprs/print/:id"
          element={
            <ProtectedRoute>
              <BPRSFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Patient Health Questionnaire (PHQ-9) */}
        <Route
          path="/phase04/phq"
          element={
            <ProtectedRoute>
              <PHQ />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/phq/add"
          element={
            <ProtectedRoute>
              <AddPHQ />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/phq/edit/:id"
          element={
            <ProtectedRoute>
              <EditPHQ />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/phq/print/:id"
          element={
            <ProtectedRoute>
              <PHQFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Obsessive Compulsive Test - Yale Brown OCD Scale (YBOCS) */}
        <Route
          path="/phase04/obsessive-compulsive-test"
          element={
            <ProtectedRoute>
              <ObsessiveCompulsiveTest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/obsessive-compulsive-test/add"
          element={
            <ProtectedRoute>
              <AddObsessiveCompulsiveTest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/obsessive-compulsive-test/edit/:id"
          element={
            <ProtectedRoute>
              <EditObsessiveCompulsiveTest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/obsessive-compulsive-test/print/:id"
          element={
            <ProtectedRoute>
              <ObsessiveCompulsiveTestFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Quality of Life Scale (QOLS) */}
        <Route
          path="/phase04/qol"
          element={
            <ProtectedRoute>
              <QOL />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/qol/add"
          element={
            <ProtectedRoute>
              <AddQol />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/qol/edit/:id"
          element={
            <ProtectedRoute>
              <EditQOL />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/qol/print/:id"
          element={
            <ProtectedRoute>
              <Qol2FormPrint />
            </ProtectedRoute>
          }
        />
        {/* Young Mania Rating Scale (YMRS) */}
        <Route
          path="/phase04/ymrs"
          element={
            <ProtectedRoute>
              <YMRS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/ymrs/add"
          element={
            <ProtectedRoute>
              <AddYMRS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/ymrs/edit/:id"
          element={
            <ProtectedRoute>
              <EditYMRS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase04/ymrs/print/:id"
          element={
            <ProtectedRoute>
              <YMRSFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Psychiatrist Order */}
        <Route
          path="/phase03/psychiatrist-order"
          element={
            <ProtectedRoute>
              <Psychiatrists />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/psychiatrist-order/add"
          element={
            <ProtectedRoute>
              <AddPsych />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/psychiatrist-order/edit/:id"
          element={
            <ProtectedRoute>
              <EditPsych />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/psychiatrist-order/print/:id"
          element={
            <ProtectedRoute>
              <PsychFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Complaint Form */}
        <Route
          path="/phase03/complaint-form"
          element={
            <ProtectedRoute>
              <Complaint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/complaint-form/add"
          element={
            <ProtectedRoute>
              <AddComplaint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/complaint-form/edit/:id"
          element={
            <ProtectedRoute>
              <EditComplaint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phase03/complaint-form/print/:id"
          element={
            <ProtectedRoute>
              <ComplaintFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Health & Safety Disclosure */}
        <Route
          path="/home-visit/health-safety"
          element={
            <ProtectedRoute>
              <HealthSafety />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visit/health-safety/add"
          element={
            <ProtectedRoute>
              <AddHealthSafety />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visit/health-safety/edit/:id"
          element={
            <ProtectedRoute>
              <EditHealthSafety />
            </ProtectedRoute>
          }
        />
        {/* Treatment Plan */}
        <Route
          path="/home-visit/treatment-plan"
          element={
            <ProtectedRoute>
              <TreatmentPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visit/treatment-plan/add"
          element={
            <ProtectedRoute>
              <AddTreatmentPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home-visit/treatment-plan/edit/:id"
          element={
            <ProtectedRoute>
              <EditTreatmentPlan />
            </ProtectedRoute>
          }
        />
        {/* Integrated Initial Assessment */}
        <Route
          path="/residential/phase-01/integrated-initial-assessment"
          element={
            <ProtectedRoute>
              <IntegratedInitialAssessments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/phase-01/integrated-initial-assessment/add"
          element={
            <ProtectedRoute>
              <AddIntegratedInitialAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/phase-01/integrated-initial-assessment/edit/:id"
          element={
            <ProtectedRoute>
              <EditIntegratedInitialAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/phase-01/integrated-initial-assessment/print/:id"
          element={
            <ProtectedRoute>
              <IntegratedAssessmentFormPrint />
            </ProtectedRoute>
          }
        />
        {/* LAMA Form */}
        <Route
          path="/residential/optional/lama-form"
          element={
            <ProtectedRoute>
              <Lama />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/lama-form/add"
          element={
            <ProtectedRoute>
              <AddLama />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/lama-form/edit/:id"
          element={
            <ProtectedRoute>
              <EditLama />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/lama-form/print/:id"
          element={
            <ProtectedRoute>
              <LamaFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Referral Form */}
        <Route
          path="/residential/optional/referral-form"
          element={
            <ProtectedRoute>
              <Referrals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/referral-form/add"
          element={
            <ProtectedRoute>
              <AddReferral />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/referral-form/edit/:id"
          element={
            <ProtectedRoute>
              <EditReferral />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/referral-form/print/:id"
          element={
            <ProtectedRoute>
              <ReferralFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Relocation Form */}
        <Route
          path="/residential/optional/relocation-form"
          element={
            <ProtectedRoute>
              <Relocations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/relocation-form/add"
          element={
            <ProtectedRoute>
              <AddRelocation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/relocation-form/edit/:id"
          element={
            <ProtectedRoute>
              <EditRelocation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/relocation-form/print/:id"
          element={
            <ProtectedRoute>
              <RelocationFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Safety Plan */}
        <Route
          path="/residential/optional/safety-plan"
          element={
            <ProtectedRoute>
              <SafetyPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/safety-plan/add"
          element={
            <ProtectedRoute>
              <AddSafetyPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/safety-plan/edit/:id"
          element={
            <ProtectedRoute>
              <EditSafetyPlan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/safety-plan/print/:id"
          element={
            <ProtectedRoute>
              <SafetyPlanPrint />
            </ProtectedRoute>
          }
        />
        {/* V-RISK-10 Violence Risk Assessment */}
        <Route
          path="/residential/optional/violence-risk"
          element={
            <ProtectedRoute>
              <ViolenceRisks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/violence-risk/add"
          element={
            <ProtectedRoute>
              <AddViolenceRisk />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/violence-risk/edit/:id"
          element={
            <ProtectedRoute>
              <EditViolenceRisk />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/violence-risk/print/:id"
          element={
            <ProtectedRoute>
              <ViolenceFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Suicide Risk Screener */}
        <Route
          path="/residential/optional/suicide-detail"
          element={
            <ProtectedRoute>
              <SuicideDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/suicide-detail/add"
          element={
            <ProtectedRoute>
              <AddSuicideDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/suicide-detail/edit/:id"
          element={
            <ProtectedRoute>
              <EditSuicideDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/suicide-detail/print/:id"
          element={
            <ProtectedRoute>
              <SuicideFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Weekly Exercise Schedule */}
        <Route
          path="/exercise-schedule"
          element={
            <ProtectedRoute>
              <ExerciseSchedules />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercise-schedule/add"
          element={
            <ProtectedRoute>
              <AddExerciseSchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercise-schedule/edit/:id"
          element={
            <ProtectedRoute>
              <EditExerciseSchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercise-schedule/print/:id"
          element={
            <ProtectedRoute>
              <ExerciseFormPrint />
            </ProtectedRoute>
          }
        />
        {/* Safety Plan (Urdu) */}
        <Route
          path="/residential/optional/safety-plan-urdu"
          element={
            <ProtectedRoute>
              <SafetyPlansUrdu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/safety-plan-urdu/add"
          element={
            <ProtectedRoute>
              <AddSafetyPlanUrdu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/residential/optional/safety-plan-urdu/edit/:id"
          element={
            <ProtectedRoute>
              <EditSafetyPlanUrdu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optional/safety-plan-urdu/print/:id"
          element={
            <ProtectedRoute>
              <SafetyPlanUrduFormPrint />
            </ProtectedRoute>
          }
        />

        {/* Nursing OPD Assessment Routes */}
        <Route
          path="/nursing-opd-assessments"
          element={
            <ProtectedRoute>
              <NursingOPDAssessments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursing-opd-assessments/add"
          element={
            <ProtectedRoute>
              <AddNursingOPDAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursing-opd-assessments/edit/:id"
          element={
            <ProtectedRoute>
              <EditNursingOPDAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursing-opd-assessments/print/:id"
          element={
            <ProtectedRoute>
              <NursingOPDPrint />
            </ProtectedRoute>
          }
        />

        {/* Doctor OPD Assessment Routes */}
        <Route
          path="/doctor-opd-assessments"
          element={
            <ProtectedRoute>
              <DoctorOPDAssessments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor-opd-assessments/add"
          element={
            <ProtectedRoute>
              <AddDoctorOPDAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor-opd-assessments/edit/:id"
          element={
            <ProtectedRoute>
              <EditDoctorOPDAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor-opd-assessments/print/:id"
          element={
            <ProtectedRoute>
              <DoctorOPDPrint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor-opd-assessments/prescription-print/:id"
          element={
            <ProtectedRoute>
              <PrescriptionSlipPrint />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div >
  );
};

function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppRoutes />
      </Router>
    </Provider>
  );
}

export default App;